/**
 * GuestTrayPage — Mobile-first PWA opened from QR code on a physical tray.
 * Route: /t/:trayId
 *
 * Public (no auth). Shows:
 *   - Hotel name (dynamic from DB — never hardcoded)
 *   - Room number
 *   - Product list with Card / BHERMES pricing
 *   - Cart / open items from IoT events
 *   - Embedded Bitsy concierge chat
 *   - Pay buttons (Stripe fiat / BHERMES on-chain)
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Loader2, CreditCard, Zap, MessageCircle, ShoppingBag, Plus, Minus,
  Check, X, Sparkles, MapPin, Package,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { BACKEND_URL } from '@/lib/backendUrl';
import MetaverseChat from '@/components/metaverse/MetaverseChat';

export default function GuestTrayPage() {
  const { trayId } = useParams();
  const [params] = useSearchParams();
  const stayId = params.get('stay_id') || null;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tray, setTray] = useState(null);
  const [cartQtys, setCartQtys] = useState({}); // slot_id -> qty
  const [chatOpen, setChatOpen] = useState(false);
  const [paying, setPaying] = useState(false);
  const [paymentMsg, setPaymentMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = stayId ? `?stay_id=${encodeURIComponent(stayId)}` : '';
      const r = await fetch(`${BACKEND_URL}/api/tray/public/${trayId}${qs}`);
      if (!r.ok) throw new Error(`Tray not found (${r.status})`);
      const d = await r.json();
      setTray(d);
      // Preload open items into cart
      const pre = {};
      (d.cart || []).forEach((c) => { pre[c.slot_id] = 1; });
      setCartQtys(pre);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [trayId, stayId]);

  useEffect(() => { load(); }, [load]);

  const cartItems = useMemo(() => {
    if (!tray) return [];
    return (tray.products || [])
      .map((p) => ({ ...p, qty: cartQtys[p.slot_id] || 0 }))
      .filter((p) => p.qty > 0);
  }, [tray, cartQtys]);

  const totals = useMemo(() => {
    const fiat = cartItems.reduce((s, i) => s + i.price_usd * i.qty, 0);
    const bhermes = cartItems.reduce((s, i) => s + i.price_bhermes * i.qty, 0);
    return {
      fiat: Math.round(fiat * 100) / 100,
      bhermes: Math.round(bhermes * 100) / 100,
      savings: Math.round((fiat - bhermes) * 100) / 100,
    };
  }, [cartItems]);

  const inc = (slotId) => setCartQtys((q) => ({ ...q, [slotId]: (q[slotId] || 0) + 1 }));
  const dec = (slotId) => setCartQtys((q) => ({ ...q, [slotId]: Math.max(0, (q[slotId] || 0) - 1) }));

  const payFiat = async () => {
    if (!cartItems.length) return;
    setPaying(true);
    setPaymentMsg(null);
    try {
      const body = {
        hotel_id: tray.bitsy_context.hotel_id,
        room_number: tray.room_number,
        items: cartItems.map((c) => ({
          product_id: c.sku,
          product_name: c.product_name,
          qty: c.qty,
        })),
        origin_url: window.location.origin,
      };
      const r = await fetch(`${BACKEND_URL}/api/fridge/checkout/stripe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (d.checkout_url) {
        window.location.href = d.checkout_url;
      } else {
        setPaymentMsg({ type: 'error', text: d.detail || 'Could not start checkout' });
      }
    } catch (e) {
      setPaymentMsg({ type: 'error', text: e.message });
    } finally {
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0e1a] text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#F7931A]" data-testid="tray-loading" />
      </div>
    );
  }

  if (error || !tray) {
    return (
      <div className="min-h-screen bg-[#0a0e1a] text-white flex items-center justify-center p-6">
        <div className="max-w-md text-center" data-testid="tray-error">
          <Package className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-semibold mb-2">Tray unavailable</h1>
          <p className="text-sm text-[#94a3b8]">{error || 'This tray is not registered yet.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-white pb-28" data-testid="guest-tray-page">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-[#0a0e1a]/90 backdrop-blur border-b border-[#1a2235] px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#F7931A] to-amber-600 flex items-center justify-center shrink-0">
            <Package className="w-5 h-5 text-black" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-wider text-[#64748b]">You're in</p>
            <h1 className="text-sm font-semibold truncate" data-testid="hotel-name">{tray.hotel.name}</h1>
            <div className="flex items-center gap-1 text-[10px] text-[#94a3b8]">
              <MapPin className="w-2.5 h-2.5" />
              <span>Room {tray.room_number}</span>
              {tray.label && <><span>·</span><span>{tray.label}</span></>}
            </div>
          </div>
        </div>
      </header>

      {/* Products */}
      <section className="px-4 py-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold">Tray items</h2>
          <Badge className="bg-cyan-500/10 text-cyan-400 text-[10px]">
            Pay with $BHERMES · save 25%
          </Badge>
        </div>
        <div className="space-y-2">
          {tray.products.map((p) => {
            const qty = cartQtys[p.slot_id] || 0;
            return (
              <div
                key={p.slot_id}
                className={`rounded-2xl p-3 border transition ${qty > 0 ? 'border-cyan-500/40 bg-cyan-500/5' : 'border-[#1a2235] bg-[#0d1220]'}`}
                data-testid={`slot-${p.slot_id}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{p.product_name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[11px] text-[#94a3b8] font-mono">${p.price_usd.toFixed(2)}</span>
                      <span className="text-[10px] text-[#64748b]">·</span>
                      <span className="text-[11px] text-cyan-400 font-mono flex items-center gap-1">
                        <Zap className="w-2.5 h-2.5" />
                        {p.price_bhermes.toFixed(2)}
                      </span>
                    </div>
                  </div>
                  {qty === 0 ? (
                    <Button
                      size="sm"
                      onClick={() => inc(p.slot_id)}
                      className="bg-[#F7931A] text-black hover:bg-[#F7931A]/90 h-8 px-3"
                      data-testid={`add-slot-${p.slot_id}`}
                    >
                      <Plus className="w-3 h-3 mr-1" /> Add
                    </Button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => dec(p.slot_id)} data-testid={`dec-slot-${p.slot_id}`}>
                        <Minus className="w-3 h-3" />
                      </Button>
                      <span className="text-sm font-semibold tabular-nums w-5 text-center">{qty}</span>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => inc(p.slot_id)} data-testid={`inc-slot-${p.slot_id}`}>
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {tray.products.length === 0 && (
            <p className="text-xs text-[#64748b] text-center py-8" data-testid="empty-tray">
              This tray is not yet stocked. Tap the chat icon to reach concierge.
            </p>
          )}
        </div>
      </section>

      {/* Bitsy CTA */}
      <section className="px-4">
        <button
          onClick={() => setChatOpen(true)}
          className="w-full rounded-2xl p-3 bg-gradient-to-r from-[#F7931A]/10 to-amber-500/10 border border-[#F7931A]/30 flex items-center gap-3 hover:border-[#F7931A]/60 transition"
          data-testid="open-bitsy-chat"
        >
          <div className="w-10 h-10 rounded-xl bg-[#F7931A] flex items-center justify-center shrink-0">
            <MessageCircle className="w-5 h-5 text-black" />
          </div>
          <div className="flex-1 text-left min-w-0">
            <p className="text-sm font-medium">Ask Bitsy</p>
            <p className="text-[11px] text-[#94a3b8] truncate">Concierge, room service, dispute an item — no app install</p>
          </div>
          <Sparkles className="w-4 h-4 text-[#F7931A] shrink-0" />
        </button>
      </section>

      {/* Sticky pay bar */}
      {cartItems.length > 0 && (
        <div className="fixed bottom-0 inset-x-0 bg-[#0a0e1a]/95 backdrop-blur border-t border-[#1a2235] p-3 z-20" data-testid="checkout-bar">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-[10px] uppercase text-[#64748b]">{cartItems.length} item{cartItems.length > 1 ? 's' : ''}</p>
              <p className="text-lg font-bold font-mono">${totals.fiat.toFixed(2)}</p>
              <p className="text-[10px] text-cyan-400">or {totals.bhermes.toFixed(2)} BHERMES (save ${totals.savings.toFixed(2)})</p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={payFiat}
                disabled={paying}
                className="bg-[#F7931A] text-black hover:bg-[#F7931A]/90"
                data-testid="pay-fiat-btn"
              >
                {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-4 h-4 mr-1" /> Card</>}
              </Button>
              <Button
                onClick={() => setPaymentMsg({ type: 'info', text: 'BHERMES on-chain pay coming soon on this device. Connect your wallet on the main site.' })}
                variant="outline"
                className="border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/10"
                data-testid="pay-bhermes-btn"
              >
                <Zap className="w-4 h-4 mr-1" /> BHERMES
              </Button>
            </div>
          </div>
          {paymentMsg && (
            <div className={`text-[11px] px-2 py-1 rounded ${paymentMsg.type === 'error' ? 'bg-red-500/10 text-red-400' : 'bg-blue-500/10 text-blue-400'}`} data-testid="payment-msg">
              {paymentMsg.text}
            </div>
          )}
        </div>
      )}

      {/* Bitsy chat overlay */}
      {chatOpen && (
        <div className="fixed inset-0 bg-black/80 z-30 flex items-end sm:items-center sm:justify-center" data-testid="bitsy-chat-modal">
          <div className="w-full sm:max-w-md h-[80vh] sm:h-[600px] bg-[#0a0e1a] border border-[#1a2235] rounded-t-2xl sm:rounded-2xl flex flex-col">
            <div className="flex items-center justify-between p-3 border-b border-[#1a2235]">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-[#F7931A] flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-black" />
                </div>
                <div>
                  <p className="text-sm font-semibold">Bitsy</p>
                  <p className="text-[9px] text-[#64748b]">{tray.hotel.name} concierge</p>
                </div>
              </div>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setChatOpen(false)} data-testid="close-bitsy">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex-1 overflow-hidden">
              <MetaverseChat
                hotelId={tray.bitsy_context.hotel_id}
                roomType={`tray-${trayId}`}
                guestId={null}
                initialGreeting={`Hi! I can help with anything at ${tray.hotel.name} — Room ${tray.room_number}. Want to review your tray cart, order room service, or ask about the hotel?`}
                onClose={() => setChatOpen(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
